import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { UserPlus, Pencil, Trash2, KeyRound, Building, Plus, X, Smartphone, Fingerprint, Mail, Link2 } from 'lucide-react';
import HorarioSemanalInput from './HorarioSemanalInput';

const MODOS_HORAS_OPTS = [
  { v: 'generico', label: 'Genérico (mismas horas todos los días)' },
  { v: 'por_dia', label: 'Por día de la semana (personalizado)' },
  { v: 'heredar_departamento', label: 'Heredar del departamento principal' },
  { v: 'heredar_centro', label: 'Heredar del centro de trabajo' },
];

const ModoHorasSelector = ({ value, onChange, testidPrefix = 'modo' }) => (
  <div className="flex flex-col gap-1.5">
    {MODOS_HORAS_OPTS.map((o) => (
      <label key={o.v} className={`flex items-center gap-2 text-sm cursor-pointer p-2 rounded border ${value === o.v ? 'border-zinc-900 bg-zinc-50' : 'border-zinc-200'}`} data-testid={`${testidPrefix}-${o.v}`}>
        <input type="radio" checked={value === o.v} onChange={() => onChange(o.v)} className="accent-zinc-900" />
        <span>{o.label}</span>
      </label>
    ))}
  </div>
);


const DeptChecklist = ({ departamentos, seleccion, onToggle, prefix = 'dept' }) => (
  <div className="flex flex-wrap gap-2">
    {departamentos.length === 0 && <p className="text-xs text-zinc-400">No hay departamentos. Créalos con el botón &quot;Departamentos&quot;.</p>}
    {departamentos.map((d) => {
      const on = seleccion.includes(d.id);
      return (
        <button type="button" key={d.id} onClick={() => onToggle(d.id)}
          data-testid={`${prefix}-chip-${d.id}`}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-300'}`}>
          {d.nombre}
        </button>
      );
    })}
  </div>
);

const EmpleadosTab = () => {
  const [empleados, setEmpleados] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [centros, setCentros] = useState([]);
  const [nuevo, setNuevo] = useState({ full_name: '', username: '', email: '', pin: '', horas_esperadas: 8, departamento_ids: [], centro_id: '', modo_horas: 'generico', horas_por_dia: {}, departamento_principal_id: '' });
  const [editando, setEditando] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: '', email: '', pin: '', horas_esperadas: 8, departamento_ids: [], centro_id: '', modo_horas: 'generico', horas_por_dia: {}, departamento_principal_id: '' });
  const [open, setOpen] = useState(false);
  const [filtroDept, setFiltroDept] = useState('todos');
  const [depOpen, setDepOpen] = useState(false);
  const [nuevoDep, setNuevoDep] = useState('');
  const [nuevoDepHorario, setNuevoDepHorario] = useState({});
  const [depEditando, setDepEditando] = useState(null); // {id, nombre, horario_semanal}
  const [huellas, setHuellas] = useState(null);
  const [pinResult, setPinResult] = useState(null); // { emp, pin_nuevo } cuando el email no se envía
  const [reactivar, setReactivar] = useState(null); // { empleado_id, full_name, baja_at } cuando el DNI existe inactivo

  // Validación de DNI/NIE español (letra correcta según módulo 23)
  const validarDni = (v) => {
    const s = (v || '').toUpperCase().replace(/[-\s]/g, '');
    const m = /^([XYZ]?)(\d{7,8})([A-Z])$/.exec(s);
    if (!m) return false;
    const letras = 'TRWAGMYFPDXBNJZSQVHLCKE';
    let [, pref, digits, letra] = m;
    if (pref) {
      if (digits.length !== 7) return false;
      digits = { X: '0', Y: '1', Z: '2' }[pref] + digits;
    } else if (digits.length !== 8) return false;
    return letras[parseInt(digits, 10) % 23] === letra;
  };

  const depName = (id) => departamentos.find((d) => d.id === id)?.nombre || '';
  const centroName = (id) => centros.find((c) => c.id === id)?.nombre || '';
  const cargar = () => api.get('/admin/empleados').then((r) => setEmpleados(r.data)).catch(() => {});
  const cargarDeps = () => api.get('/admin/departamentos').then((r) => setDepartamentos(r.data)).catch(() => {});
  const cargarCentros = () => api.get('/admin/centros').then((r) => setCentros(r.data)).catch(() => {});
  useEffect(() => { cargar(); cargarDeps(); cargarCentros(); }, []);

  const crear = async (e) => {
    e.preventDefault();
    // Validaciones cliente (feedback rápido; el backend valida igualmente)
    if (!validarDni(nuevo.username)) {
      toast.error('El DNI/NIE no es válido. Revisa el número y la letra.');
      return;
    }
    if (!nuevo.email?.trim()) {
      toast.error('El email es obligatorio');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(nuevo.email.trim())) {
      toast.error('El email no tiene un formato válido');
      return;
    }
    try {
      await api.post('/admin/empleados', { ...nuevo, username: nuevo.username.toUpperCase().replace(/[-\s]/g, ''), centro_id: nuevo.centro_id || null });
      toast.success('Empleado creado correctamente');
      setNuevo({ full_name: '', username: '', email: '', pin: '', horas_esperadas: 8, departamento_ids: [], centro_id: '', modo_horas: 'generico', horas_por_dia: {}, departamento_principal_id: '' });
      cargar(); cargarDeps();
    } catch (err) {
      const d = err?.response?.data?.detail;
      // Caso especial: existe un empleado inactivo con este DNI → ofrecer reactivación
      if (err?.response?.status === 409 && d?.code === 'empleado_inactivo_existente') {
        setReactivar({
          empleado_id: d.empleado_id,
          full_name: d.full_name,
          baja_at: d.baja_at,
          nuevo_full_name: nuevo.full_name,
        });
        return;
      }
      toast.error(typeof d === 'string' ? d : 'Error al crear empleado');
    }
  };

  const confirmarReactivacion = async () => {
    if (!reactivar) return;
    try {
      await api.post(`/admin/empleados/${reactivar.empleado_id}/reactivar`);
      toast.success(`Empleado "${reactivar.full_name}" reactivado`);
      setReactivar(null);
      setNuevo({ full_name: '', username: '', email: '', pin: '', horas_esperadas: 8, departamento_ids: [], centro_id: '', modo_horas: 'generico', horas_por_dia: {}, departamento_principal_id: '' });
      cargar();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'No se pudo reactivar');
    }
  };

  const abrirEdicion = (emp) => {
    setEditando(emp);
    setEditForm({
      full_name: emp.full_name,
      email: emp.email || '',
      pin: '',
      horas_esperadas: emp.horas_esperadas ?? 8,
      departamento_ids: emp.departamento_ids || [],
      centro_id: emp.centro_id || '',
      modo_horas: emp.modo_horas || 'generico',
      horas_por_dia: emp.horas_por_dia || {},
      departamento_principal_id: emp.departamento_principal_id || '',
    });
    setHuellas(null);
    api.get(`/admin/empleados/${emp.id}/webauthn`).then((r) => setHuellas(r.data)).catch(() => setHuellas([]));
    setOpen(true);
  };

  const revocarHuella = async (credId) => {
    try {
      await api.delete(`/admin/webauthn/${credId}`);
      setHuellas((prev) => prev.filter((h) => h.id !== credId));
      toast.success('Huella revocada. El empleado deberá usar su PIN.');
    } catch { toast.error('Error al revocar la huella'); }
  };

  const copiarLinkApp = async () => {
    const url = `${window.location.origin}/app`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Enlace de la app copiado: ${url}`);
    } catch {
      toast.info(`Enlace de la app: ${url}`);
    }
  };

  const enviarInvitacion = async (emp) => {
    if (!emp.email) {
      toast.error('Este empleado no tiene email. Añádelo en Editar empleado antes de enviar la invitación.');
      return;
    }
    try {
      await api.post(`/admin/empleados/${emp.id}/invitacion-app`, { app_url: `${window.location.origin}/app` });
      toast.success(`Email de bienvenida enviado a ${emp.email}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'No se pudo enviar el email');
    }
  };

  const reenviarPin = async (emp) => {
    try {
      const r = await api.post(`/admin/empleados/${emp.id}/reenviar-pin`);
      if (r.data?.email_enviado) {
        toast.success(`Nuevo PIN enviado por email a ${r.data.email}`);
      } else {
        setPinResult({ emp, pin_nuevo: r.data?.pin_nuevo, email: r.data?.email });
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'No se pudo generar el nuevo PIN');
    }
  };

  const copiarPin = async () => {
    if (!pinResult?.pin_nuevo) return;
    try {
      await navigator.clipboard.writeText(pinResult.pin_nuevo);
      toast.success('PIN copiado al portapapeles');
    } catch {
      toast.info(`PIN: ${pinResult.pin_nuevo}`);
    }
  };

  const guardarEdicion = async () => {
    try {
      const payload = {
        full_name: editForm.full_name,
        email: editForm.email,
        departamento_ids: editForm.departamento_ids,
        centro_id: editForm.centro_id || null,
        modo_horas: editForm.modo_horas,
      };
      if (editForm.modo_horas === 'generico') {
        payload.horas_esperadas = parseFloat(editForm.horas_esperadas);
      }
      if (editForm.modo_horas === 'por_dia') {
        payload.horas_por_dia = editForm.horas_por_dia || {};
      }
      if (editForm.modo_horas === 'heredar_departamento') {
        if (!editForm.departamento_principal_id) {
          toast.error('Selecciona el departamento principal del que se heredará el horario.');
          return;
        }
        payload.departamento_principal_id = editForm.departamento_principal_id;
      }
      if (editForm.pin) payload.pin = editForm.pin;
      await api.put(`/admin/empleados/${editando.id}`, payload);
      toast.success('Empleado actualizado');
      setOpen(false); cargar(); cargarDeps();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar');
    }
  };

  const toggleActivo = async (emp) => {
    try { await api.put(`/admin/empleados/${emp.id}`, { activo: !emp.activo }); cargar(); }
    catch (err) { toast.error('Error al cambiar estado'); }
  };

  const toggleApp = async (emp) => {
    try {
      await api.put(`/admin/empleados/${emp.id}`, { app_permitida: !emp.app_permitida });
      toast.success(!emp.app_permitida ? `${emp.full_name} ya puede usar la app` : `Acceso a la app retirado a ${emp.full_name}`);
      cargar();
    } catch (err) { toast.error('Error al cambiar el permiso de app'); }
  };

  const toggleAppDept = async (depId, valor) => {
    try {
      const r = await api.post('/admin/empleados/app-permiso-departamento', { departamento_id: depId, app_permitida: valor });
      toast.success(`${valor ? 'Activado' : 'Desactivado'} el acceso a la app para ${r.data.afectados} empleado(s)`);
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al aplicar el permiso'); }
  };

  const eliminar = async (emp) => {
    try { await api.delete(`/admin/empleados/${emp.id}`); toast.success('Empleado eliminado'); cargar(); cargarDeps(); }
    catch (err) { toast.error('Error al eliminar'); }
  };

  const crearDep = async () => {
    if (!nuevoDep.trim()) return;
    try { await api.post('/admin/departamentos', { nombre: nuevoDep.trim(), horario_semanal: nuevoDepHorario }); setNuevoDep(''); setNuevoDepHorario({}); cargarDeps(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error al crear departamento'); }
  };
  const eliminarDep = async (id) => {
    try { await api.delete(`/admin/departamentos/${id}`); cargarDeps(); cargar(); }
    catch (err) { toast.error('Error al eliminar departamento'); }
  };

  const toggleNuevoDept = (id) => setNuevo((n) => ({ ...n, departamento_ids: n.departamento_ids.includes(id) ? n.departamento_ids.filter((x) => x !== id) : [...n.departamento_ids, id] }));
  const toggleEditDept = (id) => setEditForm((f) => ({ ...f, departamento_ids: f.departamento_ids.includes(id) ? f.departamento_ids.filter((x) => x !== id) : [...f.departamento_ids, id] }));

  const visibles = filtroDept === 'todos' ? empleados
    : filtroDept === 'sin' ? empleados.filter((e) => !(e.departamento_ids || []).length)
    : empleados.filter((e) => (e.departamento_ids || []).includes(filtroDept));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="empleados-tab">
      <Card className="border-zinc-200 lg:col-span-1 h-fit">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2 text-lg">
            <UserPlus className="h-5 w-5" /> Nuevo empleado
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={crear} className="space-y-3">
            <div className="space-y-1.5">
              <Label>Nombre completo</Label>
              <Input data-testid="new-emp-fullname" value={nuevo.full_name}
                onChange={(e) => setNuevo({ ...nuevo, full_name: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label>DNI</Label>
              <Input data-testid="new-emp-username" value={nuevo.username}
                onChange={(e) => setNuevo({ ...nuevo, username: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" data-testid="new-emp-email" value={nuevo.email} required
                placeholder="empleado@empresa.com"
                onChange={(e) => setNuevo({ ...nuevo, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>PIN (4-6 dígitos)</Label>
              <Input data-testid="new-emp-pin" inputMode="numeric" maxLength={6} value={nuevo.pin}
                onChange={(e) => setNuevo({ ...nuevo, pin: e.target.value.replace(/\D/g, '') })} required />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Building className="h-4 w-4" /> Departamentos</Label>
              <DeptChecklist departamentos={departamentos} seleccion={nuevo.departamento_ids} onToggle={toggleNuevoDept} prefix="new-dept" />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Building className="h-4 w-4" /> Centro de trabajo</Label>
              <Select value={nuevo.centro_id || 'sin'} onValueChange={(v) => setNuevo({ ...nuevo, centro_id: v === 'sin' ? '' : v })}>
                <SelectTrigger data-testid="new-emp-centro"><SelectValue placeholder="Sin centro" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sin">Sin centro</SelectItem>
                  {centros.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 pt-2 border-t border-zinc-100">
              <Label>Jornada esperada — modo</Label>
              <ModoHorasSelector
                value={nuevo.modo_horas}
                onChange={(v) => setNuevo({ ...nuevo, modo_horas: v })}
                testidPrefix="new-emp"
              />
              {nuevo.modo_horas === 'generico' && (
                <div>
                  <Label className="text-xs">Horas por día (mismas todos los días)</Label>
                  <Input data-testid="new-emp-horas" type="number" min="0" max="24" step="0.5" value={nuevo.horas_esperadas}
                    onChange={(e) => setNuevo({ ...nuevo, horas_esperadas: parseFloat(e.target.value || '0') })} required />
                </div>
              )}
              {nuevo.modo_horas === 'por_dia' && (
                <div>
                  <Label className="text-xs">Horas por día de la semana</Label>
                  <HorarioSemanalInput value={nuevo.horas_por_dia} onChange={(h) => setNuevo({ ...nuevo, horas_por_dia: h })} testidPrefix="new-emp-hs" />
                  <p className="text-[11px] text-zinc-500 mt-1">Deja un día en blanco para marcarlo como no laborable (no se aplicará autocierre).</p>
                </div>
              )}
              {nuevo.modo_horas === 'heredar_departamento' && (
                <div>
                  <Label className="text-xs">Departamento principal (fuente del horario)</Label>
                  <Select value={nuevo.departamento_principal_id || ''} onValueChange={(v) => setNuevo({ ...nuevo, departamento_principal_id: v })}>
                    <SelectTrigger data-testid="new-emp-dep-principal"><SelectValue placeholder="Elige uno..." /></SelectTrigger>
                    <SelectContent>
                      {nuevo.departamento_ids.map((id) => {
                        const d = departamentos.find((x) => x.id === id);
                        if (!d) return null;
                        const tiene = Object.values(d.horario_semanal || {}).some((v) => (v || 0) > 0);
                        return <SelectItem key={id} value={id} disabled={!tiene}>{d.nombre} {!tiene && '(sin horario)'}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-zinc-500 mt-1">Solo se listan los departamentos ya asignados al empleado y con horario configurado.</p>
                </div>
              )}
              {nuevo.modo_horas === 'heredar_centro' && (
                <p className="text-xs text-zinc-500">Heredará el horario del centro seleccionado arriba. El centro debe tener horario configurado.</p>
              )}
            </div>
            <Button type="submit" className="w-full bg-zinc-900 hover:bg-zinc-800" data-testid="create-emp-btn">
              Crear empleado
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="border-zinc-200 lg:col-span-2">
        <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
          <CardTitle className="font-heading text-lg">Empleados ({visibles.length})</CardTitle>
          <div className="flex items-center gap-2">
            <Select value={filtroDept} onValueChange={setFiltroDept}>
              <SelectTrigger className="w-[190px]" data-testid="filtro-departamento"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los departamentos</SelectItem>
                <SelectItem value="sin">Sin departamento</SelectItem>
                {departamentos.map((d) => <SelectItem key={d.id} value={d.id}>{d.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => setDepOpen(true)} data-testid="gestionar-deps-btn">
              <Building className="h-4 w-4 mr-1.5" /> Departamentos
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-zinc-50">
                <tr className="text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  <th className="py-3 px-5">Nombre</th>
                  <th className="py-3 px-5">Departamentos</th>
                  <th className="py-3 px-5">Centro</th>
                  <th className="py-3 px-5">App móvil</th>
                  <th className="py-3 px-5">Estado</th>
                  <th className="py-3 px-5 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((emp) => (
                  <tr key={emp.id} className="border-t border-zinc-100" data-testid={`emp-row-${emp.username}`}>
                    <td className="py-3 px-5">
                      <div className="font-medium text-zinc-900">{emp.full_name}</div>
                      <div className="text-xs text-zinc-500">@{emp.username}</div>
                    </td>
                    <td className="py-3 px-5">
                      <div className="flex flex-wrap gap-1">
                        {(emp.departamento_ids || []).length === 0 && <span className="text-xs text-zinc-300">—</span>}
                        {(emp.departamento_ids || []).map((id) => (
                          <Badge key={id} variant="secondary" className="text-xs">{depName(id)}</Badge>
                        ))}
                      </div>
                    </td>
                    <td className="py-3 px-5">
                      {emp.centro_id ? <Badge variant="outline" className="text-xs">{centroName(emp.centro_id)}</Badge> : <span className="text-xs text-zinc-300">—</span>}
                    </td>
                    <td className="py-3 px-5">
                      <div className="flex items-center gap-2">
                        <Switch checked={!!emp.app_permitida} onCheckedChange={() => toggleApp(emp)}
                          data-testid={`toggle-app-${emp.username}`} />
                        <Smartphone className={`h-4 w-4 ${emp.app_permitida ? 'text-green-600' : 'text-zinc-300'}`} />
                        {emp.app_permitida && (
                          <>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-zinc-500 hover:text-zinc-900"
                              title={emp.email ? `Enviar email de bienvenida a ${emp.email}` : 'El empleado no tiene email'}
                              onClick={() => enviarInvitacion(emp)} data-testid={`invitar-app-${emp.username}`}>
                              <Mail className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-zinc-500 hover:text-zinc-900"
                              title="Copiar enlace de la app para enviárselo al empleado"
                              onClick={copiarLinkApp} data-testid={`copiar-link-app-${emp.username}`}>
                              <Link2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-5">
                      <button onClick={() => toggleActivo(emp)} data-testid={`toggle-activo-${emp.username}`}>
                        <Badge variant={emp.activo ? 'default' : 'secondary'} className="cursor-pointer">
                          {emp.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </button>
                    </td>
                    <td className="py-3 px-5">
                      <div className="flex justify-end gap-2">
                        <Button size="icon" variant="outline" onClick={() => abrirEdicion(emp)} data-testid={`edit-emp-${emp.username}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="icon" variant="outline" className="text-amber-600 hover:text-amber-700"
                              title={emp.email ? `Generar nuevo PIN y enviarlo a ${emp.email}` : 'Generar nuevo PIN (te lo mostraremos para dárselo al empleado)'}
                              data-testid={`reenviar-pin-${emp.username}`}>
                              <KeyRound className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Generar nuevo PIN para {emp.full_name}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Se sustituirá su PIN actual por uno nuevo de 6 dígitos.
                                {emp.email
                                  ? ` Se enviará por email a ${emp.email}.`
                                  : ' Este empleado no tiene email: te mostraremos el nuevo PIN para que se lo hagas llegar.'}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction className="bg-amber-600 hover:bg-amber-700" onClick={() => reenviarPin(emp)} data-testid={`confirm-reenviar-pin-${emp.username}`}>
                                Generar y enviar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="icon" variant="outline" className="text-red-600 hover:text-red-700" data-testid={`delete-emp-${emp.username}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar a {emp.full_name}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Se borrará el empleado y todos sus fichajes. Esta acción no se puede deshacer.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => eliminar(emp)} data-testid={`confirm-delete-${emp.username}`}>
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
                {visibles.length === 0 && (
                  <tr><td colSpan={7} className="py-8 text-center text-zinc-400">No hay empleados que mostrar.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={!!reactivar} onOpenChange={(o) => !o && setReactivar(null)}>
        <AlertDialogContent data-testid="reactivar-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Reactivar empleado existente?</AlertDialogTitle>
            <AlertDialogDescription>
              Ya existe un empleado con este DNI en tu empresa que estaba <b>dado de baja</b>:
              <br />
              <br />
              <span className="block rounded-md bg-zinc-50 border border-zinc-200 px-3 py-2 text-zinc-900">
                <b>{reactivar?.full_name}</b>
                {reactivar?.baja_at && (
                  <span className="block text-xs text-zinc-500 mt-0.5">Baja: {new Date(reactivar.baja_at).toLocaleDateString('es-ES')}</span>
                )}
              </span>
              <br />
              Puedes <b>reactivarlo</b> (recuperará su historial de fichajes y datos previos) o cancelar para usar otro DNI.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reactivar-cancelar">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-emerald-600 hover:bg-emerald-700" onClick={confirmarReactivacion} data-testid="reactivar-confirmar">
              Reactivar empleado
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!pinResult} onOpenChange={(o) => !o && setPinResult(null)}>
        <DialogContent data-testid="pin-nuevo-dialog">
          <DialogHeader>
            <DialogTitle>Nuevo PIN generado</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-zinc-600">
              {pinResult?.email
                ? 'No se pudo enviar el email (SMTP no configurado o error de envío). Facilita este PIN al empleado en persona:'
                : `Este empleado no tiene email configurado. Facilita este PIN a ${pinResult?.emp?.full_name} en persona:`}
            </p>
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-4 text-center">
              <div className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Nuevo PIN</div>
              <div className="mt-1 text-4xl font-bold tabular-nums tracking-widest text-zinc-900" data-testid="pin-nuevo-valor">
                {pinResult?.pin_nuevo}
              </div>
            </div>
            <p className="text-xs text-zinc-500">
              Por seguridad, recomienda al empleado que lo cambie desde el kiosko en su próximo fichaje.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={copiarPin} data-testid="pin-nuevo-copiar">Copiar PIN</Button>
            <Button onClick={() => setPinResult(null)} data-testid="pin-nuevo-cerrar">Hecho</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Editar empleado</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Nombre completo</Label>
              <Input data-testid="edit-emp-fullname" value={editForm.full_name}
                onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input data-testid="edit-emp-email" value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Building className="h-4 w-4" /> Departamentos</Label>
              <DeptChecklist departamentos={departamentos} seleccion={editForm.departamento_ids} onToggle={toggleEditDept} prefix="edit-dept" />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Building className="h-4 w-4" /> Centro de trabajo</Label>
              <Select value={editForm.centro_id || 'sin'} onValueChange={(v) => setEditForm({ ...editForm, centro_id: v === 'sin' ? '' : v })}>
                <SelectTrigger data-testid="edit-emp-centro"><SelectValue placeholder="Sin centro" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sin">Sin centro</SelectItem>
                  {centros.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 pt-2 border-t border-zinc-100">
              <Label>Jornada esperada — modo</Label>
              <ModoHorasSelector
                value={editForm.modo_horas}
                onChange={(v) => setEditForm({ ...editForm, modo_horas: v })}
                testidPrefix="edit-emp"
              />
              {editForm.modo_horas === 'generico' && (
                <div>
                  <Label className="text-xs">Horas por día (mismas todos los días)</Label>
                  <Input data-testid="edit-emp-horas" type="number" min="0" max="24" step="0.5" value={editForm.horas_esperadas}
                    onChange={(e) => setEditForm({ ...editForm, horas_esperadas: e.target.value })} />
                </div>
              )}
              {editForm.modo_horas === 'por_dia' && (
                <div>
                  <Label className="text-xs">Horas por día de la semana</Label>
                  <HorarioSemanalInput value={editForm.horas_por_dia} onChange={(h) => setEditForm({ ...editForm, horas_por_dia: h })} testidPrefix="edit-emp-hs" />
                  <p className="text-[11px] text-zinc-500 mt-1">Deja un día en blanco para marcarlo como no laborable (no se aplicará autocierre).</p>
                </div>
              )}
              {editForm.modo_horas === 'heredar_departamento' && (
                <div>
                  <Label className="text-xs">Departamento principal (fuente del horario)</Label>
                  <Select value={editForm.departamento_principal_id || ''} onValueChange={(v) => setEditForm({ ...editForm, departamento_principal_id: v })}>
                    <SelectTrigger data-testid="edit-emp-dep-principal"><SelectValue placeholder="Elige uno..." /></SelectTrigger>
                    <SelectContent>
                      {editForm.departamento_ids.map((id) => {
                        const d = departamentos.find((x) => x.id === id);
                        if (!d) return null;
                        const tiene = Object.values(d.horario_semanal || {}).some((v) => (v || 0) > 0);
                        return <SelectItem key={id} value={id} disabled={!tiene}>{d.nombre} {!tiene && '(sin horario)'}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-zinc-500 mt-1">Solo se listan los departamentos ya asignados al empleado y con horario configurado.</p>
                </div>
              )}
              {editForm.modo_horas === 'heredar_centro' && (
                <p className="text-xs text-zinc-500">Heredará el horario del centro seleccionado arriba. El centro debe tener horario configurado.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><KeyRound className="h-4 w-4" /> Nuevo PIN (dejar vacío para no cambiar)</Label>
              <Input data-testid="edit-emp-pin" inputMode="numeric" maxLength={6} value={editForm.pin}
                onChange={(e) => setEditForm({ ...editForm, pin: e.target.value.replace(/\D/g, '') })} />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Fingerprint className="h-4 w-4" /> Huellas registradas</Label>
              {huellas === null && <p className="text-xs text-zinc-400">Cargando…</p>}
              {huellas && huellas.length === 0 && (
                <p className="text-xs text-zinc-500" data-testid="huellas-vacio">Este empleado no tiene huellas registradas en ningún dispositivo.</p>
              )}
              {huellas && huellas.length > 0 && (
                <div className="space-y-2" data-testid="huellas-lista">
                  {huellas.map((h) => (
                    <div key={h.id} className="flex items-center justify-between rounded-lg border border-zinc-200 px-3 py-2" data-testid={`huella-${h.id}`}>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-zinc-900 truncate">{h.rp_id}</p>
                        <p className="text-[11px] text-zinc-500">
                          Registrada el {h.created_at ? new Date(h.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'}
                        </p>
                      </div>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 h-8 text-xs shrink-0"
                        onClick={() => revocarHuella(h.id)} data-testid={`revocar-huella-${h.id}`}>
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Revocar
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-zinc-900 hover:bg-zinc-800" onClick={guardarEdicion} data-testid="save-edit-emp-btn">
              Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={depOpen} onOpenChange={setDepOpen}>
        <DialogContent data-testid="deps-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Building className="h-5 w-5" /> Departamentos</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input placeholder="Nombre del departamento" value={nuevoDep}
                onChange={(e) => setNuevoDep(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); crearDep(); } }}
                data-testid="new-dep-input" />
              <Button className="bg-zinc-900 hover:bg-zinc-800" onClick={crearDep} data-testid="create-dep-btn">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <details className="text-xs">
              <summary className="cursor-pointer text-zinc-600">Configurar horario semanal (opcional)</summary>
              <div className="mt-2">
                <HorarioSemanalInput value={nuevoDepHorario} onChange={setNuevoDepHorario} testidPrefix="new-dep-hs" />
              </div>
            </details>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {departamentos.length === 0 && <p className="text-sm text-zinc-400 text-center py-4">Aún no hay departamentos.</p>}
            {departamentos.map((d) => (
              <div key={d.id} className="bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 space-y-2" data-testid={`dep-item-${d.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-zinc-800">{d.nombre}</span>
                    {Object.values(d.horario_semanal || {}).some((v) => (v || 0) > 0) && (
                      <span className="text-[10px] text-emerald-700">✓ Horario configurado</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">{d.num_empleados} empleados</Badge>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-blue-700 hover:bg-blue-50"
                      onClick={() => setDepEditando(depEditando?.id === d.id ? null : { id: d.id, nombre: d.nombre, horario_semanal: d.horario_semanal || {} })}
                      data-testid={`dep-editar-horario-${d.id}`}>
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Horario
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-green-700 hover:bg-green-50"
                      onClick={() => toggleAppDept(d.id, true)} data-testid={`dep-app-on-${d.id}`} title="Activar app a todo el departamento">
                      <Smartphone className="h-3.5 w-3.5 mr-1" /> App On
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-zinc-500 hover:bg-zinc-100"
                      onClick={() => toggleAppDept(d.id, false)} data-testid={`dep-app-off-${d.id}`} title="Quitar app a todo el departamento">
                      App Off
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => eliminarDep(d.id)} data-testid={`delete-dep-${d.id}`}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {depEditando?.id === d.id && (
                  <div className="pt-2 border-t border-zinc-200">
                    <HorarioSemanalInput
                      value={depEditando.horario_semanal}
                      onChange={(h) => setDepEditando({ ...depEditando, horario_semanal: h })}
                      testidPrefix={`dep-hs-${d.id}`}
                    />
                    <div className="flex justify-end gap-2 mt-2">
                      <Button size="sm" variant="outline" onClick={() => setDepEditando(null)}>Cancelar</Button>
                      <Button size="sm" className="bg-zinc-900"
                        onClick={async () => {
                          try {
                            await api.put(`/admin/departamentos/${d.id}`, { horario_semanal: depEditando.horario_semanal });
                            toast.success('Horario guardado');
                            setDepEditando(null); cargarDeps();
                          } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
                        }}
                        data-testid={`dep-guardar-horario-${d.id}`}>Guardar</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmpleadosTab;
